import React, { useEffect, useState } from 'react';
import './EmailSentNotifier.css';

const EmailSentNotifier = ({ status, message, onClose, scheduledTime, recipientEmail }) => {
  const [visible, setVisible] = useState(false);

  // Animation beim Erscheinen
  useEffect(() => {
    if (status) {
      setVisible(true);
      
      // Bestimme die Anzeigedauer: Länger für geplante E-Mails (8 Sekunden) und Zusammenfassungen (6 Sekunden)
      let displayDuration = 4000; // Standard: 4 Sekunden
      
      if (scheduledTime && status === 'success') {
        displayDuration = 8000; // Geplante E-Mails: 8 Sekunden
      } else if (status === 'summary') {
        displayDuration = 6000; // Zusammenfassungen: 6 Sekunden
      } else if (status === 'safeguard_rejected') {
        displayDuration = 7000; // SafeGuard Ablehnungen: 7 Sekunden (länger für Details)
      }
      
      // Automatisches Ausblenden
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          // Erst nach der Ausblenden-Animation den Status zurücksetzen
          if (onClose) onClose();
        }, 300); // Warte auf das Abschluss der Animation
      }, displayDuration);
      
      return () => clearTimeout(timer);
    }
  }, [status, onClose, scheduledTime]);

  // Wenn kein Status gesetzt ist, nichts anzeigen
  if (!status) return null;

  // Formatiere das geplante Datum für die Anzeige
  const formatScheduledDate = (timestamp, includeTime = false) => {
    try {
      const date = new Date(parseInt(timestamp));
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Monatsnamen auf Englisch
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      if (includeTime) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}. ${month} ${year}, ${hours}:${minutes}h`;
      }
      
      return `${day}. ${month} ${year}`;
    } catch (error) {
      console.error('Error formatting scheduled date:', error);
      return 'Invalid date';
    }
  };

  // Berechne eine freundlichere Zeitanzeige (z.B. "in 5 minutes")
  const getRelativeTimeString = (timestamp) => {
    console.log("Berechne relative Zeit für:", timestamp, "Typ:", typeof timestamp);
    
    if (!timestamp) return '';
    
    try {
      const scheduledDate = new Date(timestamp);
      console.log("Umgewandelt in relative Date:", scheduledDate.toString(), "Gültig:", !isNaN(scheduledDate.getTime()));
      
      const now = new Date();
      
      // Differenz in Millisekunden
      const diffMs = scheduledDate.getTime() - now.getTime();
      
      // Verschiedene Zeiteinheiten
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      const diffHours = Math.round(diffMs / (1000 * 60 * 60));
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      
      // Benutzerfreundlicher String
      if (diffMinutes < 60) {
        return `in ${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`;
      } else if (diffHours < 24) {
        return `in ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
      } else {
        return `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
      }
    } catch (error) {
      return '';
    }
  };

  // Extrahiere E-Mail-Adresse aus der Nachricht, wenn keine explizit übergeben wurde
  const extractEmailFromMessage = () => {
    if (recipientEmail) return recipientEmail;
    
    try {
      // Versuche, die E-Mail-Adresse vom Parameter zu nutzen
      console.log("Extrahiere E-Mail aus:", message);
      
      // 1. Prüfe die E-Mail in den Debug-Logs
      if (message && message.includes("to:")) {
        const toMatch = message.match(/to:\s*([^\s]+@[^\s]+)/);
        if (toMatch && toMatch[1]) {
          console.log("E-Mail aus Debug-Log gefunden:", toMatch[1]);
          return toMatch[1];
        }
      }
      
      // 2. Direkte E-Mail in der Nachricht
      if (message && message.includes("@")) {
        const emailMatch = message.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
        if (emailMatch && emailMatch[1]) {
          console.log("E-Mail direkt in der Nachricht gefunden:", emailMatch[1]);
          return emailMatch[1];
        }
      }
      
      // 3. Versuche, den Empfängernamen zu extrahieren, wenn keine E-Mail gefunden wurde
      if (message && message.includes("to ") && message.includes("scheduled")) {
        const match = message.match(/to\s+([^]+?)\s+scheduled/);
        if (match && match[1]) {
          console.log("Empfängername gefunden:", match[1]);
          return match[1];
        }
      }
    } catch (error) {
      console.error("Fehler beim Extrahieren der E-Mail:", error);
    }
    
    return '';
  };
  
  const emailAddress = extractEmailFromMessage();

  return (
    <div className={`email-sent-notifier ${status} ${visible ? 'visible' : 'hidden'} ${scheduledTime ? 'scheduled' : ''}`}>
      <div className="email-sent-notifier-icon">
        {status === 'success' ? (
          scheduledTime ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )
        ) : status === 'summary' ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 12h6M9 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : status === 'safeguard_rejected' ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9v2m0 4h.01M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div className="email-sent-notifier-content">
        <p className="email-sent-notifier-title">
          {status === 'success' ? 
            (scheduledTime ? `Email scheduled${emailAddress ? ` `  : ''}` : 'Email sent') : 
            status === 'summary' ? 'Summary' : 
            status === 'safeguard_rejected' ? 'SafeGuard Rejected' : 'Email error'}
          {emailAddress && scheduledTime && 
            <span className="email-address-display">({emailAddress})</span>
          }
        </p>
        <p className="email-sent-notifier-message">
          {message}
          {scheduledTime && status === 'success' && (
            <>
              <span className="scheduled-time-info">
                <span role="img" aria-label="Clock"></span>{' '}
                <strong>scheduled for {formatScheduledDate(scheduledTime, true)}</strong>
              </span>
              <span style={{display: 'none'}}>DEBUG: ScheduledTime: {scheduledTime}, Typ: {typeof scheduledTime}</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default EmailSentNotifier; 